import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
  type AccountRow,
  type TransactionRow,
  useAccounts,
  useCreateTransaction,
  useTransactions,
} from "../../../hooks/useAccounting";
import {
  type CommissionPayoutRow,
  useCommissionHistory,
} from "../../../hooks/useCommission";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getTransactions } from "../../../server/accounting";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/accounting/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.accounting.transactions({ userId: session.user.id }),
      queryFn: async () => {
        const result = await getTransactions({
          data: { userId: session.user.id, accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.transactions;
      },
      staleTime: STALE_TIMES.moderate,
    });
  },
  pendingComponent: () => <PageSkeleton rows={6} variant="list" />,
  errorComponent: createRouteError("Accounting"),
  component: AccountingPage,
});

// ── Helpers ───────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<TransactionRow["type"], string> = {
  income: "Income",
  expense: "Expense",
  transfer: "Transfer",
  adjustment: "Adjustment",
};

const TYPE_COLORS: Record<TransactionRow["type"], string> = {
  income:
    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  expense: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  transfer:
    "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  adjustment:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
};

const STATUS_COLORS: Record<string, string> = {
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  approved:
    "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
};

const AMOUNT_COLORS: Record<TransactionRow["type"], string> = {
  income: "text-green-600",
  expense: "text-red-600",
  transfer: "text-foreground",
  adjustment: "text-foreground",
};

const AMOUNT_SIGNS: Record<TransactionRow["type"], string> = {
  income: "+",
  expense: "−",
  transfer: "",
  adjustment: "",
};

function formatCurrency(amount: number, currency = "AUD") {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgo() {
  return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

// ── Main Page ─────────────────────────────────────────────────────────────

function AccountingPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return (
    <AuthenticatedAccounting
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

// ── Authenticated sub-component ───────────────────────────────────────────

type ActiveTab = "transactions" | "commission";

function AuthenticatedAccounting({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const auth = { userId, accessToken };

  const [activeTab, setActiveTab] = useState<ActiveTab>("transactions");

  // Transaction filters
  const [from, setFrom] = useState(thirtyDaysAgo());
  const [to, setTo] = useState(today());
  const [typeFilter, setTypeFilter] = useState<
    TransactionRow["type"] | undefined
  >(undefined);

  const [showAddForm, setShowAddForm] = useState(false);

  const { data: transactions, isLoading: txLoading, error: txError } =
    useTransactions({ ...auth, from, to, type: typeFilter });

  const { data: accounts, isLoading: accLoading } = useAccounts(auth);

  const createMutation = useCreateTransaction(auth);

  // Add form state
  const [newType, setNewType] =
    useState<TransactionRow["type"]>("expense");
  const [newAmount, setNewAmount] = useState("");
  const [newDate, setNewDate] = useState(today());
  const [newDescription, setNewDescription] = useState("");
  const [newAccountId, setNewAccountId] = useState("");

  const handleAdd = async () => {
    const amount = parseFloat(newAmount);
    if (!newAccountId || isNaN(amount) || amount <= 0) return;
    await createMutation.mutateAsync({
      type: newType,
      accountId: newAccountId,
      amount,
      date: newDate,
      description: newDescription.trim() || null,
    });
    setNewAmount("");
    setNewDescription("");
    setShowAddForm(false);
  };

  // Commission tab data
  const { data: payouts, isLoading: payoutsLoading, error: payoutsError } =
    useCommissionHistory(auth);

  const income =
    transactions
      ?.filter((t) => t.type === "income")
      .reduce((s, t) => s + t.amount, 0) ?? 0;

  const expense =
    transactions
      ?.filter((t) => t.type === "expense")
      .reduce((s, t) => s + t.amount, 0) ?? 0;

  return (
    <div>
      {/* Header */}
      <main className="container mx-auto p-6 space-y-6 max-w-4xl">
        {/* Tab bar */}
        <div className="flex gap-1 border-b">
          {(["transactions", "commission"] as const).map((tab) => (
            <Button
              key={tab}
              variant="ghost"
              onClick={() => setActiveTab(tab)}
              className={`h-auto rounded-none hover:bg-transparent px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "transactions" ? "Transactions" : "Commission History"}
            </Button>
          ))}
        </div>

        {/* ── Transactions Tab ── */}
        {activeTab === "transactions" && (
          <div className="space-y-4">
            {/* Summary cards */}
            {!txLoading && transactions && (
              <div className="grid grid-cols-3 gap-4">
                <SummaryCard label="Income" value={formatCurrency(income)} color="text-green-600" />
                <SummaryCard label="Expenses" value={formatCurrency(expense)} color="text-red-600" />
                <SummaryCard
                  label="Net"
                  value={formatCurrency(income - expense)}
                  color={income - expense >= 0 ? "text-green-600" : "text-red-600"}
                />
              </div>
            )}

            {/* Add form */}
            {showAddForm && (
              <AddTransactionForm
                accounts={accounts ?? []}
                accountsLoading={accLoading}
                type={newType}
                amount={newAmount}
                date={newDate}
                description={newDescription}
                accountId={newAccountId}
                onTypeChange={setNewType}
                onAmountChange={setNewAmount}
                onDateChange={setNewDate}
                onDescriptionChange={setNewDescription}
                onAccountChange={setNewAccountId}
                onSubmit={handleAdd}
                isPending={createMutation.isPending}
              />
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="filter-from" className="text-xs text-muted-foreground">From:</Label>
                <Input
                  id="filter-from"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="filter-to" className="text-xs text-muted-foreground">To:</Label>
                <Input
                  id="filter-to"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">Type:</span>
                {([undefined, "income", "expense", "transfer", "adjustment"] as const).map(
                  (t) => (
                    <Button
                      key={String(t)}
                      variant="ghost"
                      onClick={() => setTypeFilter(t)}
                      className={`h-auto px-2.5 py-1 text-xs rounded-full transition-colors ${
                        typeFilter === t
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      {t === undefined ? "All" : TYPE_LABELS[t]}
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Loading */}
            {txLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            )}

            {/* Error */}
            {txError && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
                {txError.message}
              </div>
            )}

            {/* Empty */}
            {!txLoading && !txError && transactions?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="text-4xl mb-3">💰</span>
                <p className="text-muted-foreground text-sm">
                  {typeFilter
                    ? `No ${TYPE_LABELS[typeFilter].toLowerCase()} transactions in this period.`
                    : "No transactions in this period."}
                </p>
              </div>
            )}

            {/* Transaction list */}
            {!txLoading && transactions && transactions.length > 0 && (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">
                        Date
                      </th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">
                        Description
                      </th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground hidden sm:table-cell">
                        Type
                      </th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions.map((tx) => (
                      <TransactionRow key={tx.id} tx={tx} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {transactions && transactions.length > 0 && (
              <p className="text-xs text-center text-muted-foreground">
                {transactions.length} transaction
                {transactions.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        )}

        {/* ── Commission History Tab ── */}
        {activeTab === "commission" && (
          <div className="space-y-4">
            {payoutsLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            )}

            {payoutsError && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
                {payoutsError.message}
              </div>
            )}

            {!payoutsLoading && !payoutsError && (!payouts || payouts.length === 0) && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="text-4xl mb-3">🏆</span>
                <p className="text-muted-foreground text-sm">
                  No commission payouts yet.
                </p>
              </div>
            )}

            {!payoutsLoading && payouts && payouts.length > 0 && (
              <div className="space-y-3">
                {payouts.map((payout) => (
                  <PayoutCard key={payout.id} payout={payout} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-lg font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function TransactionRow({ tx }: { tx: TransactionRow }) {
  return (
    <tr className="hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
        {tx.date}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        {tx.description || <span className="text-muted-foreground italic">—</span>}
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        <span
          className={`inline-flex px-2 py-0.5 text-xs rounded-full ${TYPE_COLORS[tx.type]}`}
        >
          {TYPE_LABELS[tx.type]}
        </span>
      </td>
      <td
        className={`px-4 py-3 text-sm font-medium text-right ${AMOUNT_COLORS[tx.type]}`}
      >
        {AMOUNT_SIGNS[tx.type]}
        {formatCurrency(tx.amount, tx.currency)}
      </td>
    </tr>
  );
}

function PayoutCard({ payout }: { payout: CommissionPayoutRow }) {
  const statusColor =
    STATUS_COLORS[payout.status] ??
    "bg-muted text-muted-foreground";

  return (
    <div className="rounded-lg border bg-card p-4 flex items-center gap-4">
      <div className="flex-1 space-y-0.5">
        <p className="text-sm font-medium text-foreground">
          {new Date(payout.period_start).toLocaleDateString("en-AU", {
            month: "short",
            year: "numeric",
          })}{" "}
          →{" "}
          {new Date(payout.period_end).toLocaleDateString("en-AU", {
            month: "short",
            year: "numeric",
          })}
        </p>
        <p className="text-xs text-muted-foreground">
          Created {new Date(payout.created_at ?? "").toLocaleDateString("en-AU")}
        </p>
      </div>
      <div className="text-right space-y-1">
        <p className="text-base font-semibold text-foreground">
          {formatCurrency(payout.total_amount)}
        </p>
        <span
          className={`inline-flex px-2 py-0.5 text-xs rounded-full capitalize ${statusColor}`}
        >
          {payout.status}
        </span>
      </div>
    </div>
  );
}

function AddTransactionForm({
  accounts,
  accountsLoading,
  type,
  amount,
  date,
  description,
  accountId,
  onTypeChange,
  onAmountChange,
  onDateChange,
  onDescriptionChange,
  onAccountChange,
  onSubmit,
  isPending,
}: {
  accounts: AccountRow[];
  accountsLoading: boolean;
  type: TransactionRow["type"];
  amount: string;
  date: string;
  description: string;
  accountId: string;
  onTypeChange: (v: TransactionRow["type"]) => void;
  onAmountChange: (v: string) => void;
  onDateChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onAccountChange: (v: string) => void;
  onSubmit: () => void;
  isPending: boolean;
}) {
  const isValid =
    !!accountId && parseFloat(amount) > 0 && !!date;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <h2 className="text-sm font-semibold text-card-foreground">
        New Transaction
      </h2>

      {/* Type selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Type:</span>
        {(["income", "expense", "transfer", "adjustment"] as const).map((t) => (
          <Button
            key={t}
            variant="ghost"
            onClick={() => onTypeChange(t)}
            className={`h-auto px-2.5 py-1 text-xs rounded-full capitalize transition-colors ${
              TYPE_COLORS[t]
            } ${type === t ? "ring-2 ring-offset-1 ring-primary" : "opacity-60"}`}
          >
            {TYPE_LABELS[t]}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Amount */}
        <div className="space-y-1">
          <Label htmlFor="tx-amount" className="text-xs text-muted-foreground">Amount</Label>
          <Input
            id="tx-amount"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
          />
        </div>

        {/* Date */}
        <div className="space-y-1">
          <Label htmlFor="tx-date" className="text-xs text-muted-foreground">Date</Label>
          <Input
            id="tx-date"
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
          />
        </div>
      </div>

      {/* Account */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Account</Label>
        <Select
          value={accountId || "__none__"}
          onValueChange={(v) => onAccountChange(v === "__none__" ? "" : v)}
          disabled={accountsLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={accountsLoading ? "Loading accounts…" : "Select account…"} />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div className="space-y-1">
        <Label htmlFor="tx-description" className="text-xs text-muted-foreground">
          Description (optional)
        </Label>
        <Input
          id="tx-description"
          type="text"
          placeholder="What was this for?"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
        />
      </div>

      <div className="flex justify-end">
        <Button
          onClick={onSubmit}
          disabled={!isValid}
          loading={isPending}
          size="sm"
        >
          Add Transaction
        </Button>
      </div>
    </div>
  );
}
